import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "./data";
import { useScrollTo } from "./primitives";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="sticky-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200 bg-white/80 backdrop-blur-xl"
          : "border-b border-transparent bg-white/0"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="header-logo"
          onClick={() => go("home")}
          className="flex items-center"
          aria-label="PipeMitra home"
        >
          <img
            src="/pipemitra-logo.png"
            alt="PipeMitra"
            className={`h-8 w-auto transition-all duration-300 sm:h-9 ${
              scrolled ? "" : "drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
            }`}
          />
        </button>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className={`group relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                scrolled
                  ? "text-slate-600 hover:text-[#1a6fc4]"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
              <span
                className={`absolute inset-x-4 -bottom-0.5 h-px scale-x-0 transition-transform duration-300 group-hover:scale-x-100 ${
                  scrolled ? "bg-[#1a6fc4]" : "bg-white"
                }`}
              />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-discover-btn"
            onClick={() => go("about")}
            className="group hidden items-center gap-2 rounded-full bg-[#1a6fc4] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#155a9e] active:scale-[0.97] sm:inline-flex"
          >
            Discover PipeMitra
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("about")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#1a6fc4] px-5 py-3 text-sm font-semibold text-white"
              >
                Discover PipeMitra
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
